import { supabase } from "@/integrations/supabase/client";
import {
  hasCoachFirstTakeResponse,
  normalizeCoachFirstTakeRecord,
  type CoachFirstTakeRecordRow,
} from "@/lib/coachFirstTake";
import { buildCoachLeaderAccessSnapshot } from "@/lib/coachHealth/buildCoachLeaderAccessSnapshot";
import { GRACE_PERIOD_DAYS } from "@/lib/coachHealth/buildCoachLeaderAccessSnapshot";
import {
  COACH_HEALTH_EVIDENCE_INPUT_VERSION,
  type CoachHealthEvidenceAdapterInputV2,
  type CoachHealthEvidenceInput,
  type CoachHealthRelationshipLifecycle,
  type CoachHealthRelationshipInput,
  type CoachHealthSourceReadinessInput,
  type CoachHealthTranscriptCallLinkLineageInput,
} from "@/lib/coachHealth/coachHealthEvidenceProfileContracts";
import {
  COACH_HEALTH_EVIDENCE_SOURCE_TYPES,
  isApprovedCoachingContinuityMomentSource,
  isCoachHealthTranscriptReviewable,
} from "@/lib/coachHealth/coachHealthEvidenceSourceRegistry";
import { buildCoachHealthScopeKey } from "@/lib/coachHealth/coachHealthScope";
import {
  hasCoachBillingPaidAccess,
  hasValidOptionalBillingTimestamps,
} from "@/lib/soloLeaderBilling";
import { addDays } from "date-fns";
import { filterCanonicalCoachCallTranscriptLinks } from "@/lib/transcripts/coachCallTranscriptLinkAuthorization";

const DEFAULT_PAGE_SIZE = 500;
const MAX_PAGE_SIZE = 1_000;
const IN_BATCH_SIZE = 100;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

type UnknownRow = Record<string, unknown>;
type QueryFilter =
  | { readonly kind: "eq"; readonly column: string; readonly value: unknown }
  | { readonly kind: "in"; readonly column: string; readonly value: readonly unknown[] };

interface ExactQueryResult<Row extends UnknownRow = UnknownRow> {
  readonly data: Row[] | null;
  readonly count: number | null;
  readonly error: unknown;
  readonly status?: number;
  readonly statusText?: string;
}

interface ExactQuery<Row extends UnknownRow = UnknownRow> {
  eq(column: string, value: unknown): ExactQuery<Row>;
  in(column: string, value: readonly unknown[]): ExactQuery<Row>;
  lte(column: string, value: string): ExactQuery<Row>;
  order(column: string, options: { ascending: boolean }): ExactQuery<Row>;
  range(from: number, to: number): Promise<ExactQueryResult<Row>>;
}

interface ExactTable<Row extends UnknownRow = UnknownRow> {
  select(columns: string, options: { count: "exact" }): ExactQuery<Row>;
}

interface CoachHealthContinuityMomentRow extends UnknownRow {
  readonly id: string;
  readonly kind: string;
  readonly coach_id: string;
  readonly relationship_leader_id: string;
  readonly source: string | null;
  readonly source_table: string | null;
  readonly source_id: string | null;
  readonly occurred_at: string;
}

interface ContinuityMomentsClient {
  from(table: "continuity_moments"): ExactTable<CoachHealthContinuityMomentRow>;
}

type GeneratedExactTableName =
  | "coach_leader_links"
  | "profiles"
  | "subscriptions"
  | "coach_lens"
  | "coach_calls"
  | "coach_call_participants"
  | "coach_call_transcript_links"
  | "meeting_artifact_intakes"
  | "coach_leader_session_records"
  | "transcript_uploads";

type ExactTableName = GeneratedExactTableName | "continuity_moments";

interface ExactReadSpec {
  readonly table: ExactTableName;
  readonly columns: string;
  readonly timestampColumn: string;
  readonly filters?: readonly QueryFilter[];
  readonly orders: readonly string[];
  readonly uniqueKey: string;
}

interface SourceRows {
  readonly rows: readonly UnknownRow[];
  readonly failed: boolean;
}

interface RelationshipContext {
  readonly coachId: string;
  readonly leaderId: string;
  readonly revision: string;
  readonly createdAt: string;
  readonly lifecycle: CoachHealthRelationshipLifecycle;
}

type PortfolioLifecycle = CoachHealthRelationshipLifecycle | "deleted";
type CoachHealthCallStatus = "scheduled" | "completed" | "cancelled";

export interface CoachHealthEvidenceInputSnapshot {
  readonly input: CoachHealthEvidenceAdapterInputV2;
  readonly leaderLabelsById: Readonly<Record<string, string>>;
  readonly timeZone: string;
}

export interface FetchCoachHealthEvidenceInputParams {
  readonly authenticatedCoachId: string;
  readonly asOf: string;
  readonly pageSize?: number;
  readonly authorizationLeaderId?: string;
}

export interface CoachHealthRelationshipAccessSnapshot {
  readonly asOf: string;
  readonly relationships: readonly CoachHealthRelationshipInput[];
  readonly readableLeaderIds: readonly string[];
  readonly nextHardGateAt: string | null;
}

export interface CoachHealthRelationshipAccessSnapshot {
  readonly asOf: string;
  readonly relationships: readonly CoachHealthRelationshipInput[];
  readonly readableLeaderIds: readonly string[];
  readonly nextHardGateAt: string | null;
}

class PrivateSourceReadError extends Error {}

export class CoachHealthEvidenceIncompleteError extends Error {
  readonly code = "coach_health_snapshot_incomplete" as const;

  constructor() {
    super("Coach Health evidence is temporarily unavailable.");
    this.name = "CoachHealthEvidenceIncompleteError";
  }
}

function incomplete(): never {
  throw new CoachHealthEvidenceIncompleteError();
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function stringValue(value: unknown): string {
  if (typeof value !== "string") incomplete();
  return value;
}

function callStatusValue(value: unknown): CoachHealthCallStatus {
  const status = stringValue(value);
  if (
    status !== "scheduled" &&
    status !== "completed" &&
    status !== "cancelled"
  ) {
    incomplete();
  }
  return status;
}

function nullableString(value: unknown): string | null {
  if (value === null) return null;
  return stringValue(value);
}

function booleanValue(value: unknown): boolean {
  if (typeof value !== "boolean") incomplete();
  return value;
}

function nullableBoolean(value: unknown): boolean | null {
  if (value === null) return null;
  return booleanValue(value);
}

function numberValue(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) incomplete();
  return value;
}

function uuidValue(value: unknown): string {
  if (!isUuid(value)) incomplete();
  return value;
}

interface ParsedTimestamptz {
  readonly epochMicros: number;
  readonly canonicalUtc: string;
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  const days = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month - 1] ?? 0;
}

function parseTimestamptz(value: unknown): ParsedTimestamptz | null {
  if (typeof value !== "string") return null;
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/,
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  const fractionMicros = Number((match[7] ?? "").padEnd(6, "0") || "0");
  const zone = match[8];
  let offsetMinutes = 0;
  if (zone !== "Z") {
    const offsetHour = Number(zone.slice(1, 3));
    const offsetMinute = Number(zone.slice(4, 6));
    if (offsetHour > 23 || offsetMinute > 59) return null;
    const direction = zone[0] === "+" ? 1 : -1;
    offsetMinutes = direction * (offsetHour * 60 + offsetMinute);
  }

  const local = new Date(0);
  local.setUTCFullYear(year, month - 1, day);
  local.setUTCHours(hour, minute, second, 0);
  const epochMicros =
    local.getTime() * 1_000 +
    fractionMicros -
    offsetMinutes * 60 * 1_000_000;
  if (!Number.isSafeInteger(epochMicros)) return null;

  const utcDate = new Date(Math.floor(epochMicros / 1_000));
  const utcYear = utcDate.getUTCFullYear();
  if (Number.isNaN(utcDate.getTime()) || utcYear < 1 || utcYear > 9999) {
    return null;
  }
  const iso = utcDate.toISOString();
  const microsWithinSecond =
    ((epochMicros % 1_000_000) + 1_000_000) % 1_000_000;
  const canonicalUtc =
    microsWithinSecond % 1_000 === 0
      ? iso
      : `${iso.slice(0, 19)}.${String(microsWithinSecond).padStart(6, "0")}Z`;
  return { epochMicros, canonicalUtc };
}

function canonicalTimestamp(value: unknown, asOfTime: number): string {
  const parsed = parseTimestamptz(value);
  if (!parsed || parsed.epochMicros > asOfTime) incomplete();
  return parsed.canonicalUtc;
}

function earlierTimestamp(left: string, right: string): string {
  const leftParsed = parseTimestamptz(left);
  const rightParsed = parseTimestamptz(right);
  if (!leftParsed || !rightParsed) incomplete();
  return leftParsed.epochMicros <= rightParsed.epochMicros ? left : right;
}

function canonicalAsOf(value: string): { readonly value: string; readonly time: number } {
  const parsed = parseTimestamptz(value);
  if (!parsed || parsed.canonicalUtc !== value) incomplete();
  return { value: parsed.canonicalUtc, time: parsed.epochMicros };
}

function stableValue(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableValue).join(",")}]`;
  const row = value as UnknownRow;
  return `{${Object.keys(row)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableValue(row[key])}`)
    .join(",")}}`;
}

function fingerprint(rows: readonly UnknownRow[]): string {
  return rows.map(stableValue).sort().join("\n");
}

function continuityClient(): ContinuityMomentsClient {
  return supabase as unknown as ContinuityMomentsClient;
}

function generatedQuery(spec: ExactReadSpec): ExactQuery {
  switch (spec.table) {
    case "coach_leader_links":
      return supabase.from("coach_leader_links").select(spec.columns, { count: "exact" });
    case "profiles":
      return supabase.from("profiles").select(spec.columns, { count: "exact" });
    case "subscriptions":
      return supabase.from("subscriptions").select(spec.columns, { count: "exact" });
    case "coach_lens":
      return supabase.from("coach_lens").select(spec.columns, { count: "exact" });
    case "coach_calls":
      return supabase.from("coach_calls").select(spec.columns, { count: "exact" });
    case "coach_call_participants":
      return supabase.from("coach_call_participants").select(spec.columns, { count: "exact" });
    case "coach_call_transcript_links":
      return supabase.from("coach_call_transcript_links").select(spec.columns, { count: "exact" });
    case "meeting_artifact_intakes":
      return supabase.from("meeting_artifact_intakes").select(spec.columns, { count: "exact" });
    case "coach_leader_session_records":
      return supabase.from("coach_leader_session_records").select(spec.columns, { count: "exact" });
    case "transcript_uploads":
      return supabase.from("transcript_uploads").select(spec.columns, { count: "exact" });
    case "continuity_moments":
      incomplete();
  }
}

function buildQuery(
  spec: ExactReadSpec,
  asOf: string | null,
): ExactQuery {
  let query = spec.table === "continuity_moments"
    ? continuityClient().from("continuity_moments").select(spec.columns, { count: "exact" })
    : generatedQuery(spec);
  for (const filter of spec.filters ?? []) {
    query = filter.kind === "eq"
      ? query.eq(filter.column, filter.value)
      : query.in(filter.column, filter.value);
  }
  if (asOf !== null) {
    query = query.lte(spec.timestampColumn, asOf);
  }
  for (const column of spec.orders) {
    query = query.order(column, { ascending: true });
  }
  return query;
}

async function readExactRows(params: {
  readonly spec: ExactReadSpec;
  readonly asOf: string | null;
  readonly pageSize: number;
  readonly sourceRead: boolean;
}): Promise<readonly UnknownRow[]> {
  const rows: UnknownRow[] = [];
  const identifiers = new Set<string>();
  let expectedCount: number | null = null;
  let from = 0;

  while (expectedCount === null || rows.length < expectedCount) {
    let result: ExactQueryResult;
    try {
      result = await buildQuery(params.spec, params.asOf)
        .range(from, from + params.pageSize - 1);
    } catch {
      if (params.sourceRead) throw new PrivateSourceReadError();
      incomplete();
    }
    if (result.error) {
      if (params.sourceRead) throw new PrivateSourceReadError();
      incomplete();
    }
    if (
      result.status !== undefined &&
      (result.status < 200 || result.status >= 300)
    ) {
      if (params.sourceRead) throw new PrivateSourceReadError();
      incomplete();
    }
    if (!Array.isArray(result.data)) incomplete();
    if (
      !Number.isSafeInteger(result.count) ||
      result.count === null ||
      result.count < 0
    ) {
      incomplete();
    }
    if (expectedCount === null) expectedCount = result.count;
    else if (result.count !== expectedCount) incomplete();
    if (result.data.length === 0 && rows.length < expectedCount) incomplete();

    for (const row of result.data) {
      if (!row || typeof row !== "object" || Array.isArray(row)) incomplete();
      const identifier = stringValue(row[params.spec.uniqueKey]);
      if (!identifier || identifiers.has(identifier)) incomplete();
      identifiers.add(identifier);
      rows.push(row);
    }
    if (rows.length > expectedCount) incomplete();
    from += result.data.length;
  }

  if (rows.length !== expectedCount) incomplete();
  return rows;
}

function chunks<T>(values: readonly T[]): readonly (readonly T[])[] {
  const result: T[][] = [];
  for (let index = 0; index < values.length; index += IN_BATCH_SIZE) {
    result.push(values.slice(index, index + IN_BATCH_SIZE));
  }
  return result;
}

async function readExactInBatches(params: {
  readonly spec: Omit<ExactReadSpec, "filters">;
  readonly filters?: readonly QueryFilter[];
  readonly inColumn: string;
  readonly values: readonly string[];
  readonly asOf: string;
  readonly pageSize: number;
  readonly sourceRead: boolean;
}): Promise<readonly UnknownRow[]> {
  if (params.values.length === 0) return [];
  const batchedRows: UnknownRow[] = [];
  const identifiers = new Set<string>();
  for (const batch of chunks([...new Set(params.values)].sort())) {
    const batchRows = await readExactRows({
      spec: {
        ...params.spec,
        filters: [
          ...(params.filters ?? []),
          { kind: "in", column: params.inColumn, value: batch },
        ],
      },
      asOf: params.asOf,
      pageSize: params.pageSize,
      sourceRead: params.sourceRead,
    });
    for (const row of batchRows) {
      const identifier = stringValue(row[params.spec.uniqueKey]);
      if (identifiers.has(identifier)) incomplete();
      identifiers.add(identifier);
      batchedRows.push(row);
    }
  }
  return batchedRows;
}

async function sourceRows(
  operation: () => Promise<readonly UnknownRow[]>,
): Promise<SourceRows> {
  try {
    return { rows: await operation(), failed: false };
  } catch (error) {
    if (error instanceof PrivateSourceReadError) {
      return { rows: [], failed: true };
    }
    throw error;
  }
}

function lifecycleValue(value: unknown): PortfolioLifecycle {
  if (
    value !== "active" &&
    value !== "paused" &&
    value !== "completed" &&
    value !== "archived" &&
    value !== "deleted"
  ) {
    incomplete();
  }
  return value;
}

function relationMap<T>(values: readonly T[], key: (value: T) => string): Map<string, T> {
  const result = new Map<string, T>();
  for (const value of values) {
    const identifier = key(value);
    if (result.has(identifier)) incomplete();
    result.set(identifier, value);
  }
  return result;
}

function subscriptionLeaderLimit(row: UnknownRow | null, asOf: string): number | null {
  if (!row) return 1;
  const status = stringValue(row.status);
  const currentPeriodEnd = nullableString(row.current_period_end);
  const cancelAtPeriodEnd = booleanValue(row.cancel_at_period_end);
  const paymentFailureGraceEndsAt = nullableString(row.payment_failure_grace_ends_at);
  if (!hasValidOptionalBillingTimestamps({
    currentPeriodEnd,
    paymentFailureGraceEndsAt,
  })) {
    incomplete();
  }
  nullableString(row.plan_type);
  numberValue(row.leader_quantity);
  const isPaid = hasCoachBillingPaidAccess({
    status,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    paymentFailureGraceEndsAt,
    now: new Date(asOf),
  });
  if (!isPaid) return 1;
  return null;
}

function validateSubscriptionRows(
  rows: readonly UnknownRow[],
  coachId: string,
  asOfTime: number,
): void {
  for (const row of rows) {
    uuidValue(row.id);
    if (uuidValue(row.user_id) !== coachId) incomplete();
    canonicalTimestamp(row.updated_at, asOfTime);
  }
}

function publicTimeZone(value: unknown): string {
  const candidate = nullableString(value)?.trim();
  if (!candidate) return "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format();
    return candidate;
  } catch {
    return "UTC";
  }
}

function sourceReadiness(
  failed: ReadonlySet<string>,
  unavailableSourceTypes: ReadonlySet<(typeof COACH_HEALTH_EVIDENCE_SOURCE_TYPES)[number]> = new Set(),
): CoachHealthSourceReadinessInput[] {
  const dependencies: Record<(typeof COACH_HEALTH_EVIDENCE_SOURCE_TYPES)[number], readonly string[]> = {
    coach_philosophy: ["coach_lens"],
    completed_coach_call: ["coach_calls", "coach_call_participants"],
    coach_transcript_session_record: [
      "coach_leader_session_records",
      "transcript_uploads",
      "coach_call_transcript_links",
      "coach_calls",
      "coach_call_participants",
    ],
    coach_first_take: [
      "coach_leader_session_records",
      "coach_calls",
      "coach_call_participants",
    ],
    coach_reviewable_transcript: [
      "transcript_uploads",
      "coach_leader_session_records",
      "coach_call_transcript_links",
      "coach_calls",
      "coach_call_participants",
    ],
    coaching_continuity_moment: [
      "continuity_moments",
      "coach_leader_session_records",
      "transcript_uploads",
      "coach_call_transcript_links",
      "coach_calls",
      "coach_call_participants",
    ],
  };
  return COACH_HEALTH_EVIDENCE_SOURCE_TYPES.map((sourceType) => ({
    sourceType,
    state: unavailableSourceTypes.has(sourceType) ||
      dependencies[sourceType].some((table) => failed.has(table))
      ? "error"
      : "ready",
  }));
}

export async function fetchCoachHealthRelationshipAccessSnapshot(
  params: FetchCoachHealthEvidenceInputParams,
): Promise<CoachHealthRelationshipAccessSnapshot> {
  try {
    const coachId = uuidValue(params.authenticatedCoachId);
    const asOf = canonicalAsOf(params.asOf);
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    if (!Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > MAX_PAGE_SIZE) {
      incomplete();
    }

    const startAuth = await supabase.auth.getUser();
    if (startAuth.error || startAuth.data.user?.id !== coachId) incomplete();

    const linkSpec: ExactReadSpec = {
      table: "coach_leader_links",
      columns: "coach_id, leader_id, created_at, relationship_revision",
      timestampColumn: "created_at",
      filters: [{ kind: "eq", column: "coach_id", value: coachId }],
      orders: ["created_at", "leader_id"],
      uniqueKey: "leader_id",
    };
    const links = await readExactRows({
      spec: linkSpec,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const leaderIds = links.map((row) => uuidValue(row.leader_id));
    for (const row of links) {
      if (uuidValue(row.coach_id) !== coachId) incomplete();
      uuidValue(row.relationship_revision);
      canonicalTimestamp(row.created_at, asOf.time);
    }

    const profileSpec = {
      table: "profiles",
      columns: "id, full_name, lifecycle_state, timezone, updated_at",
      timestampColumn: "updated_at",
      orders: ["id"],
      uniqueKey: "id",
    } as const;
    const profileIds = [coachId, ...leaderIds];
    const profiles = await readExactInBatches({
      spec: profileSpec,
      inColumn: "id",
      values: profileIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const profilesById = relationMap(profiles, (row) => uuidValue(row.id));
    if (profilesById.size !== new Set(profileIds).size) incomplete();

    const subscriptionSpec: ExactReadSpec = {
      table: "subscriptions",
      columns: "id, user_id, status, current_period_end, cancel_at_period_end, payment_failure_grace_ends_at, plan_type, leader_quantity, updated_at",
      timestampColumn: "updated_at",
      filters: [{ kind: "eq", column: "user_id", value: coachId }],
      orders: ["user_id"],
      uniqueKey: "user_id",
    };
    const subscriptions = await readExactRows({
      spec: subscriptionSpec,
      asOf: null,
      pageSize,
      sourceRead: false,
    });
    if (subscriptions.length > 1) incomplete();
    validateSubscriptionRows(subscriptions, coachId, asOf.time);

    const relationships: RelationshipContext[] = links.flatMap((link) => {
      const leaderId = uuidValue(link.leader_id);
      const profile = profilesById.get(leaderId);
      if (!profile) incomplete();
      canonicalTimestamp(profile.updated_at, asOf.time);
      const lifecycle = lifecycleValue(profile.lifecycle_state);
      if (lifecycle === "deleted") return [];
      return [{
        coachId,
        leaderId,
        revision: uuidValue(link.relationship_revision),
        createdAt: canonicalTimestamp(link.created_at, asOf.time),
        lifecycle,
      }];
    });
    const access = buildCoachLeaderAccessSnapshot({
      links: relationships.map((relationship) => ({
        leaderId: relationship.leaderId,
        createdAt: relationship.createdAt,
        lifecycle: relationship.lifecycle,
      })),
      leaderLimit: subscriptionLeaderLimit(subscriptions[0] ?? null, asOf.value),
      asOf: asOf.value,
    });

    const revalidatedLinks = await readExactRows({
      spec: linkSpec,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const revalidatedProfiles = await readExactInBatches({
      spec: profileSpec,
      inColumn: "id",
      values: profileIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const revalidatedSubscriptions = await readExactRows({
      spec: subscriptionSpec,
      asOf: null,
      pageSize,
      sourceRead: false,
    });
    if (revalidatedSubscriptions.length > 1) incomplete();
    validateSubscriptionRows(revalidatedSubscriptions, coachId, asOf.time);
    const endAuth = await supabase.auth.getUser();
    if (endAuth.error || endAuth.data.user?.id !== coachId) incomplete();
    if (
      fingerprint(links) !== fingerprint(revalidatedLinks) ||
      fingerprint(profiles) !== fingerprint(revalidatedProfiles) ||
      fingerprint(subscriptions) !== fingerprint(revalidatedSubscriptions)
    ) {
      incomplete();
    }

    const relationshipInputs = relationships.map((relationship) => ({
      coachId,
      leaderId: relationship.leaderId,
      relationshipRevision: relationship.revision,
      relationshipStartedAt: relationship.createdAt,
      lifecycle: relationship.lifecycle,
      accessState: access.accessByLeaderId.get(relationship.leaderId) ?? "hard_gated",
      authorized: true,
      profileResolved: true,
    })) satisfies CoachHealthRelationshipInput[];
    const readableLeaderIds = relationshipInputs
      .filter((relationship) =>
        relationship.accessState === "readable" ||
        relationship.accessState === "grace_period",
      )
      .map((relationship) => relationship.leaderId)
      .sort();
    const nextHardGateAt = [...access.gatedLeaders.values()]
      .filter((entry) => !entry.isHardGated)
      .map((entry) => addDays(entry.joinedAt, GRACE_PERIOD_DAYS).toISOString())
      .sort()[0] ?? null;

    return {
      asOf: asOf.value,
      relationships: relationshipInputs,
      readableLeaderIds,
      nextHardGateAt,
    };
  } catch (error) {
    if (error instanceof CoachHealthEvidenceIncompleteError) throw error;
    throw new CoachHealthEvidenceIncompleteError();
  }
}

export async function fetchCoachHealthEvidenceInput(
  params: FetchCoachHealthEvidenceInputParams,
): Promise<CoachHealthEvidenceInputSnapshot> {
  try {
    const coachId = uuidValue(params.authenticatedCoachId);
    const asOf = canonicalAsOf(params.asOf);
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    if (!Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > MAX_PAGE_SIZE) {
      incomplete();
    }

    const startAuth = await supabase.auth.getUser();
    if (startAuth.error || startAuth.data.user?.id !== coachId) incomplete();

    const linkSpec: ExactReadSpec = {
      table: "coach_leader_links",
      columns: "coach_id, leader_id, created_at, relationship_revision",
      timestampColumn: "created_at",
      filters: [{ kind: "eq", column: "coach_id", value: coachId }],
      orders: ["created_at", "leader_id"],
      uniqueKey: "leader_id",
    };
    const links = await readExactRows({
      spec: linkSpec,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const leaderIds = links.map((row) => uuidValue(row.leader_id));
    for (const row of links) {
      if (uuidValue(row.coach_id) !== coachId) incomplete();
      uuidValue(row.relationship_revision);
      canonicalTimestamp(row.created_at, asOf.time);
    }

    const profileSpec = {
      table: "profiles",
      columns: "id, full_name, lifecycle_state, timezone, updated_at",
      timestampColumn: "updated_at",
      orders: ["id"],
      uniqueKey: "id",
    } as const;
    const profileIds = [coachId, ...leaderIds];
    const profiles = await readExactInBatches({
      spec: profileSpec,
      inColumn: "id",
      values: profileIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const profilesById = relationMap(profiles, (row) => uuidValue(row.id));
    if (profilesById.size !== new Set(profileIds).size) incomplete();

    const subscriptionSpec: ExactReadSpec = {
      table: "subscriptions",
      columns: "id, user_id, status, current_period_end, cancel_at_period_end, payment_failure_grace_ends_at, plan_type, leader_quantity, updated_at",
      timestampColumn: "updated_at",
      filters: [{ kind: "eq", column: "user_id", value: coachId }],
      orders: ["user_id"],
      uniqueKey: "user_id",
    };
    const subscriptions = await readExactRows({
      spec: subscriptionSpec,
      asOf: null,
      pageSize,
      sourceRead: false,
    });
    if (subscriptions.length > 1) incomplete();
    validateSubscriptionRows(subscriptions, coachId, asOf.time);

    const coachProfile = profilesById.get(coachId);
    if (!coachProfile) incomplete();
    canonicalTimestamp(coachProfile.updated_at, asOf.time);
    const timeZone = publicTimeZone(coachProfile.timezone);

    const relationships: RelationshipContext[] = links.flatMap((link) => {
      const leaderId = uuidValue(link.leader_id);
      const profile = profilesById.get(leaderId);
      if (!profile) incomplete();
      canonicalTimestamp(profile.updated_at, asOf.time);
      const lifecycle = lifecycleValue(profile.lifecycle_state);
      if (lifecycle === "deleted") return [];
      return [{
        coachId,
        leaderId,
        revision: uuidValue(link.relationship_revision),
        createdAt: canonicalTimestamp(link.created_at, asOf.time),
        lifecycle,
      }];
    });
    const access = buildCoachLeaderAccessSnapshot({
      links: relationships.map((relationship) => ({
        leaderId: relationship.leaderId,
        createdAt: relationship.createdAt,
        lifecycle: relationship.lifecycle,
      })),
      leaderLimit: subscriptionLeaderLimit(subscriptions[0] ?? null, asOf.value),
      asOf: asOf.value,
    });
    const authorizationLeaderId = params.authorizationLeaderId
      ? uuidValue(params.authorizationLeaderId)
      : null;
    const hydratedRelationships = relationships.filter((relationship) => {
      const accessState = access.accessByLeaderId.get(relationship.leaderId);
      if (accessState !== "readable" && accessState !== "grace_period") {
        return false;
      }
      return relationship.lifecycle === "active" ||
        relationship.leaderId === authorizationLeaderId;
    });
    const relationshipLeaderIds = hydratedRelationships.map(
      (relationship) => relationship.leaderId,
    );
    const relationshipsByLeader = relationMap(
      hydratedRelationships,
      (relationship) => relationship.leaderId,
    );

    const failedTables = new Set<string>();
    const load = async (table: string, operation: () => Promise<readonly UnknownRow[]>) => {
      const result = await sourceRows(operation);
      if (result.failed) failedTables.add(table);
      return result.rows;
    };

    const lensRows = await load("coach_lens", () => readExactRows({
      spec: {
        table: "coach_lens",
        columns: "id, coach_id, coach_lens_json, coach_lens_updated_at, updated_at, is_active",
        timestampColumn: "updated_at",
        filters: [{ kind: "eq", column: "coach_id", value: coachId }],
        orders: ["updated_at", "id"],
        uniqueKey: "id",
      },
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const callRows = await load("coach_calls", () => readExactRows({
      spec: {
        table: "coach_calls",
        columns: "id, coach_id, scheduled_end, status",
        timestampColumn: "scheduled_end",
        filters: [{ kind: "eq", column: "coach_id", value: coachId }],
        orders: ["scheduled_end", "id"],
        uniqueKey: "id",
      },
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));
    const callIds = callRows.map((row) => uuidValue(row.id));

    const participantRows = await load("coach_call_participants", () => readExactInBatches({
      spec: {
        table: "coach_call_participants",
        columns: "id, coach_call_id, leader_id, created_at",
        timestampColumn: "created_at",
        orders: ["created_at", "id"],
        uniqueKey: "id",
      },
      inColumn: "coach_call_id",
      values: callIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const transcriptLinkRows = await load("coach_call_transcript_links", () => readExactInBatches({
      spec: {
        table: "coach_call_transcript_links",
        columns: "id, coach_call_id, transcript_upload_id, leader_id, link_type, created_at",
        timestampColumn: "created_at",
        orders: ["created_at", "id"],
        uniqueKey: "id",
      },
      inColumn: "coach_call_id",
      values: callIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const artifactIntakeRows = await load("meeting_artifact_intakes", () => readExactInBatches({
      spec: {
        table: "meeting_artifact_intakes",
        columns: "id, coach_id, source_object_type, source_object_id, leader_id, transcript_upload_id, status, lifecycle_status, updated_at",
        timestampColumn: "updated_at",
        orders: ["updated_at", "id"],
        uniqueKey: "id",
      },
      filters: [
        { kind: "eq", column: "coach_id", value: coachId },
        { kind: "eq", column: "source_object_type", value: "coach_call" },
      ],
      inColumn: "source_object_id",
      values: callIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const recordRows = await load("coach_leader_session_records", () => readExactInBatches({
      spec: {
        table: "coach_leader_session_records",
        columns: "id, coach_id, leader_id, source_type, transcript_upload_id, coach_review_v1, coach_note, created_at, updated_at, meeting_id, worked_on, direction, next_conversation_signals, ambiguity_flag, ambiguity_reasons, published_to_leader, leader_plan_draft_v1",
        timestampColumn: "updated_at",
        orders: ["updated_at", "id"],
        uniqueKey: "id",
      },
      filters: [{ kind: "eq", column: "coach_id", value: coachId }],
      inColumn: "leader_id",
      values: relationshipLeaderIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const eligibleTranscriptLinkRows = transcriptLinkRows.filter((row) => {
      const leaderId = nullableString(row.leader_id);
      return leaderId !== null &&
        isUuid(leaderId) &&
        relationshipsByLeader.has(leaderId);
    });
    const transcriptIds = [
      ...eligibleTranscriptLinkRows.map((row) => uuidValue(row.transcript_upload_id)),
      ...recordRows.flatMap((row) => row.transcript_upload_id === null
        ? []
        : [uuidValue(row.transcript_upload_id)]),
    ];
    const transcriptRows = await load("transcript_uploads", () => readExactInBatches({
      spec: {
        table: "transcript_uploads",
        columns: "id, leader_id, analysis_status, coach_verified, created_at, updated_at",
        timestampColumn: "updated_at",
        orders: ["updated_at", "id"],
        uniqueKey: "id",
      },
      inColumn: "id",
      values: transcriptIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const momentRows = await load("continuity_moments", () => readExactInBatches({
      spec: {
        table: "continuity_moments",
        columns: "id, kind, coach_id, relationship_leader_id, source, source_table, source_id, occurred_at",
        timestampColumn: "occurred_at",
        orders: ["occurred_at", "id"],
        uniqueKey: "id",
      },
      filters: [
        { kind: "eq", column: "kind", value: "coaching" },
        { kind: "eq", column: "coach_id", value: coachId },
      ],
      inColumn: "relationship_leader_id",
      values: relationshipLeaderIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: true,
    }));

    const revalidatedLinks = await readExactRows({
      spec: linkSpec,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const revalidatedProfiles = await readExactInBatches({
      spec: profileSpec,
      inColumn: "id",
      values: profileIds,
      asOf: asOf.value,
      pageSize,
      sourceRead: false,
    });
    const revalidatedSubscriptions = await readExactRows({
      spec: subscriptionSpec,
      asOf: null,
      pageSize,
      sourceRead: false,
    });
    if (revalidatedSubscriptions.length > 1) incomplete();
    validateSubscriptionRows(revalidatedSubscriptions, coachId, asOf.time);
    const endAuth = await supabase.auth.getUser();
    if (endAuth.error || endAuth.data.user?.id !== coachId) incomplete();
    if (
      fingerprint(links) !== fingerprint(revalidatedLinks) ||
      fingerprint(profiles) !== fingerprint(revalidatedProfiles) ||
      fingerprint(subscriptions) !== fingerprint(revalidatedSubscriptions)
    ) {
      incomplete();
    }

    const callsById = relationMap(callRows, (row) => uuidValue(row.id));
    const participantsByCall = new Map<string, UnknownRow[]>();
    const participantPairs = new Set<string>();
    for (const row of participantRows) {
      const callId = uuidValue(row.coach_call_id);
      const leaderId = uuidValue(row.leader_id);
      canonicalTimestamp(row.created_at, asOf.time);
      if (!callsById.has(callId)) incomplete();
      const pair = `${callId}:${leaderId}`;
      if (participantPairs.has(pair)) incomplete();
      participantPairs.add(pair);
      const current = participantsByCall.get(callId) ?? [];
      current.push(row);
      participantsByCall.set(callId, current);
    }

    const transcriptsById = relationMap(transcriptRows, (row) => uuidValue(row.id));
    const transcriptOriginById = new Map<string, string>();
    for (const row of transcriptRows) {
      transcriptOriginById.set(
        uuidValue(row.id),
        canonicalTimestamp(row.created_at, asOf.time),
      );
    }
    const lineageByTranscript = new Map<string, CoachHealthTranscriptCallLinkLineageInput>();
    const canonicalTranscriptLinkRows = filterCanonicalCoachCallTranscriptLinks({
      links: eligibleTranscriptLinkRows.map((row) => ({
        ...row,
        coach_call_id: uuidValue(row.coach_call_id),
        transcript_upload_id: uuidValue(row.transcript_upload_id),
        leader_id: uuidValue(row.leader_id),
        link_type: stringValue(row.link_type),
      })),
      transcripts: transcriptRows.map((row) => ({
        id: uuidValue(row.id),
        leader_id: uuidValue(row.leader_id),
      })),
      participants: participantRows.map((row) => ({
        coach_call_id: uuidValue(row.coach_call_id),
        leader_id: uuidValue(row.leader_id),
      })),
      artifactIntakes: artifactIntakeRows.map((row) => ({
        source_object_type: stringValue(row.source_object_type),
        source_object_id: nullableString(row.source_object_id),
        leader_id: nullableString(row.leader_id),
        transcript_upload_id: nullableString(row.transcript_upload_id),
        status: stringValue(row.status),
        lifecycle_status: stringValue(row.lifecycle_status),
      })),
    });
    const deduplicatedTranscriptLinkRows = [...new Map(
      canonicalTranscriptLinkRows.map((row) => [
        `${row.coach_call_id}:${row.leader_id}:${row.transcript_upload_id}`,
        row,
      ]),
    ).values()];
    if (
      !failedTables.has("coach_calls") &&
      !failedTables.has("coach_call_participants") &&
      !failedTables.has("coach_call_transcript_links") &&
      !failedTables.has("transcript_uploads")
    ) {
      for (const row of deduplicatedTranscriptLinkRows) {
        const transcriptUploadId = uuidValue(row.transcript_upload_id);
        if (lineageByTranscript.has(transcriptUploadId)) incomplete();
        const callId = uuidValue(row.coach_call_id);
        const leaderId = uuidValue(row.leader_id);
        const transcript = transcriptsById.get(transcriptUploadId);
        if (!transcript || uuidValue(transcript.leader_id) !== leaderId) incomplete();
        canonicalTimestamp(row.created_at, asOf.time);
        const call = callsById.get(callId);
        if (!call || uuidValue(call.coach_id) !== coachId) incomplete();
        if (!participantPairs.has(`${callId}:${leaderId}`)) incomplete();
        if (!relationshipsByLeader.has(leaderId)) incomplete();
        const callStatus = callStatusValue(call.status);
        const callScheduledEnd = canonicalTimestamp(
          call.scheduled_end,
          asOf.time,
        );
        lineageByTranscript.set(transcriptUploadId, {
          kind: "transcript_call_link",
          transcriptUploadId,
          callId,
          coachId,
          participantLeaderId: leaderId,
          callStatus,
          callScheduledEnd,
        });
      }
    }

    const evidence: CoachHealthEvidenceInput[] = [];
    if (!failedTables.has("coach_lens")) {
      for (const row of lensRows) {
        if (uuidValue(row.coach_id) !== coachId) incomplete();
        const updatedAt = canonicalTimestamp(row.updated_at, asOf.time);
        const lensUpdatedAt = row.coach_lens_updated_at === null
          ? null
          : canonicalTimestamp(row.coach_lens_updated_at, asOf.time);
        const lensJson = row.coach_lens_json;
        const philosophy = lensJson && typeof lensJson === "object" && !Array.isArray(lensJson)
          ? (lensJson as UnknownRow).philosophy
          : null;
        evidence.push({
          sourceType: "coach_philosophy",
          lensId: uuidValue(row.id),
          coachId,
          coachLensUpdatedAt: lensUpdatedAt,
          updatedAt,
          isActive: nullableBoolean(row.is_active) === true,
          hasPhilosophy: typeof philosophy === "string" && philosophy.trim().length > 0,
        });
      }
    }

    if (!failedTables.has("coach_calls") && !failedTables.has("coach_call_participants")) {
      for (const call of callRows) {
        const callId = uuidValue(call.id);
        if (uuidValue(call.coach_id) !== coachId) incomplete();
        const status = callStatusValue(call.status);
        const scheduledEnd = canonicalTimestamp(call.scheduled_end, asOf.time);
        if (status !== "completed") continue;
        for (const participant of participantsByCall.get(callId) ?? []) {
          const leaderId = uuidValue(participant.leader_id);
          const relationship = relationshipsByLeader.get(leaderId);
          if (!relationship) continue;
          evidence.push({
            sourceType: "completed_coach_call",
            callId,
            coachId,
            leaderId,
            relationshipRevision: relationship.revision,
            scheduledEnd,
            status: "completed",
            participantLeaderId: leaderId,
          });
        }
      }
    }

    const recordsById = relationMap(recordRows, (row) => uuidValue(row.id));
    const recordOriginById = new Map<string, string>();
    if (!failedTables.has("coach_leader_session_records")) {
      for (const row of recordRows) {
        const recordId = uuidValue(row.id);
        if (uuidValue(row.coach_id) !== coachId) incomplete();
        const leaderId = uuidValue(row.leader_id);
        const relationship = relationshipsByLeader.get(leaderId);
        if (!relationship) incomplete();
        const sourceType = stringValue(row.source_type);
        canonicalTimestamp(row.updated_at, asOf.time);
        let originAt = canonicalTimestamp(row.created_at, asOf.time);
        if (sourceType === "transcript_review") {
          if (failedTables.has("transcript_uploads")) continue;
          const transcriptUploadId = uuidValue(row.transcript_upload_id);
          const transcript = transcriptsById.get(transcriptUploadId);
          if (!transcript || uuidValue(transcript.leader_id) !== leaderId) incomplete();
          const transcriptOriginAt = transcriptOriginById.get(transcriptUploadId);
          if (!transcriptOriginAt) incomplete();
          originAt = earlierTimestamp(originAt, transcriptOriginAt);
          const callLineage = lineageByTranscript.get(transcriptUploadId) ?? null;
          if (callLineage) {
            const call = callsById.get(callLineage.callId);
            if (!call) incomplete();
            const callAt = canonicalTimestamp(call.scheduled_end, asOf.time);
            originAt = earlierTimestamp(originAt, callAt);
          }
          recordOriginById.set(recordId, originAt);
          evidence.push({
            sourceType: "coach_transcript_session_record",
            recordId,
            coachId,
            leaderId,
            relationshipRevision: relationship.revision,
            updatedAt: originAt,
            transcriptUploadId,
            recordSourceType: "transcript_review",
            callLineage,
          });
          continue;
        }
        if (sourceType !== "coach_first_take") continue;
        const normalized = normalizeCoachFirstTakeRecord(
          row as unknown as CoachFirstTakeRecordRow,
        );
        if (!normalized) continue;
        const call = callsById.get(normalized.coachCallId);
        if (!call || uuidValue(call.coach_id) !== coachId) continue;
        if (!participantPairs.has(`${normalized.coachCallId}:${leaderId}`)) continue;
        const embeddedCallStatus = callStatusValue(call.status);
        const callAt = canonicalTimestamp(call.scheduled_end, asOf.time);
        originAt = earlierTimestamp(originAt, callAt);
        recordOriginById.set(recordId, originAt);
        evidence.push({
          sourceType: "coach_first_take",
          recordId,
          coachId,
          leaderId,
          relationshipRevision: relationship.revision,
          updatedAt: originAt,
          recordSourceType: "coach_first_take",
          payloadVersion: "coach_first_take_v1",
          embeddedCallId: normalized.coachCallId,
          embeddedCallCoachId: coachId,
          embeddedParticipantLeaderId: leaderId,
          hasCoachAuthoredResponse: hasCoachFirstTakeResponse({
            answers: normalized.answers,
            coachNote: normalized.coach_note,
          }),
          embeddedCallStatus,
          embeddedCallScheduledEnd: callAt,
        });
      }
    }

    if (!failedTables.has("transcript_uploads")) {
      for (const row of transcriptRows) {
        const transcriptId = uuidValue(row.id);
        const leaderId = uuidValue(row.leader_id);
        const relationship = relationshipsByLeader.get(leaderId);
        if (!relationship) incomplete();
        const status = stringValue(row.analysis_status);
        const coachVerified = nullableBoolean(row.coach_verified);
        const transcriptOriginAt = transcriptOriginById.get(transcriptId);
        if (!transcriptOriginAt) incomplete();
        let updatedAt = earlierTimestamp(
          canonicalTimestamp(row.updated_at, asOf.time),
          transcriptOriginAt,
        );
        if (!isCoachHealthTranscriptReviewable(status, coachVerified)) continue;
        const callLineage = lineageByTranscript.get(transcriptId);
        const record = recordRows.find((candidate) =>
          candidate.source_type === "transcript_review" &&
          candidate.transcript_upload_id === transcriptId &&
          candidate.leader_id === leaderId
        );
        if (!callLineage && !record) continue;
        if (record) {
          const recordOrigin = recordOriginById.get(uuidValue(record.id));
          if (!recordOrigin) incomplete();
          updatedAt = earlierTimestamp(updatedAt, recordOrigin);
        }
        if (callLineage) {
          const call = callsById.get(callLineage.callId);
          if (!call) incomplete();
          const callAt = canonicalTimestamp(call.scheduled_end, asOf.time);
          updatedAt = earlierTimestamp(updatedAt, callAt);
        }
        evidence.push({
          sourceType: "coach_reviewable_transcript",
          transcriptId,
          coachId,
          leaderId,
          relationshipRevision: relationship.revision,
          updatedAt,
          status,
          coachVerified,
          lineage: callLineage ?? {
            kind: "session_record",
            recordId: uuidValue(record?.id),
            coachId,
            leaderId,
            recordSourceType: "transcript_review",
            transcriptUploadId: transcriptId,
            callLineage: null,
          },
        });
      }
    }

    if (
      !failedTables.has("continuity_moments") &&
      !failedTables.has("coach_leader_session_records") &&
      !failedTables.has("transcript_uploads") &&
      !failedTables.has("coach_call_transcript_links") &&
      !failedTables.has("coach_calls") &&
      !failedTables.has("coach_call_participants")
    ) {
      for (const row of momentRows) {
        if (
          row.kind !== "coaching" ||
          row.coach_id !== coachId ||
          !isApprovedCoachingContinuityMomentSource(
            nullableString(row.source_table),
            nullableString(row.source),
          )
        ) {
          continue;
        }
        const leaderId = uuidValue(row.relationship_leader_id);
        const relationship = relationshipsByLeader.get(leaderId);
        if (!relationship) incomplete();
        const sourceRecordId = uuidValue(row.source_id);
        const sourceRecord = recordsById.get(sourceRecordId);
        if (
          !sourceRecord ||
          sourceRecord.coach_id !== coachId ||
          sourceRecord.leader_id !== leaderId
        ) {
          incomplete();
        }
        const recordSourceType = stringValue(sourceRecord.source_type);
        let sourceRecordLineage;
        if (recordSourceType === "transcript_review" && sourceRecord.transcript_upload_id !== null) {
          const transcriptUploadId = uuidValue(sourceRecord.transcript_upload_id);
          const transcript = transcriptsById.get(transcriptUploadId);
          if (!transcript || uuidValue(transcript.leader_id) !== leaderId) incomplete();
          sourceRecordLineage = {
            recordId: sourceRecordId,
            coachId,
            leaderId,
            recordSourceType: "transcript_review" as const,
            transcriptUploadId,
            transcriptCallLink: lineageByTranscript.get(transcriptUploadId) ?? null,
          };
        } else if (recordSourceType === "coach_first_take") {
          const normalized = normalizeCoachFirstTakeRecord(
            sourceRecord as unknown as CoachFirstTakeRecordRow,
          );
          if (!normalized) incomplete();
          const embeddedCall = callsById.get(normalized.coachCallId);
          if (
            !embeddedCall ||
            embeddedCall.coach_id !== coachId ||
            !participantPairs.has(`${normalized.coachCallId}:${leaderId}`)
          ) {
            incomplete();
          }
          const hasResponse = hasCoachFirstTakeResponse({
            answers: normalized.answers,
            coachNote: normalized.coach_note,
          });
          const embeddedCallStatus = callStatusValue(embeddedCall.status);
          const embeddedCallScheduledEnd = canonicalTimestamp(
            embeddedCall.scheduled_end,
            asOf.time,
          );
          sourceRecordLineage = {
            recordId: sourceRecordId,
            coachId,
            leaderId,
            recordSourceType: "coach_first_take" as const,
            payloadVersion: "coach_first_take_v1" as const,
            embeddedCallId: normalized.coachCallId,
            embeddedCallCoachId: coachId,
            embeddedParticipantLeaderId: leaderId,
            hasCoachAuthoredResponse: hasResponse,
            embeddedCallStatus,
            embeddedCallScheduledEnd,
          };
        } else {
          incomplete();
        }
        const sourceOriginAt = recordOriginById.get(sourceRecordId);
        if (!sourceOriginAt) incomplete();
        const occurredAt = earlierTimestamp(
          canonicalTimestamp(row.occurred_at, asOf.time),
          sourceOriginAt,
        );
        evidence.push({
          sourceType: "coaching_continuity_moment",
          momentId: uuidValue(row.id),
          coachId,
          leaderId,
          relationshipRevision: relationship.revision,
          occurredAt,
          kind: "coaching",
          sourceTable: "coach_leader_session_records",
          source: stringValue(row.source),
          sourceRecordId,
          sourceRecordLineage,
        });
      }
    }

    const hasReviewableTranscriptEvidence = evidence.some(
      (item) => item.sourceType === "coach_reviewable_transcript",
    );
    const hasUnverifiableSoftOnlyReviewableTranscript =
      failedTables.has("meeting_artifact_intakes") &&
      !hasReviewableTranscriptEvidence &&
      eligibleTranscriptLinkRows.some((row) => {
        if (stringValue(row.link_type) !== "soft") return false;
        const callId = uuidValue(row.coach_call_id);
        const transcriptId = uuidValue(row.transcript_upload_id);
        const leaderId = uuidValue(row.leader_id);
        const transcript = transcriptsById.get(transcriptId);
        const call = callsById.get(callId);
        if (
          !transcript ||
          uuidValue(transcript.leader_id) !== leaderId ||
          !call ||
          uuidValue(call.coach_id) !== coachId ||
          !participantPairs.has(`${callId}:${leaderId}`)
        ) {
          return false;
        }
        return isCoachHealthTranscriptReviewable(
          stringValue(transcript.analysis_status),
          nullableBoolean(transcript.coach_verified),
        );
      });
    const unavailableSourceTypes = new Set<
      (typeof COACH_HEALTH_EVIDENCE_SOURCE_TYPES)[number]
    >();
    if (hasUnverifiableSoftOnlyReviewableTranscript) {
      unavailableSourceTypes.add("coach_reviewable_transcript");
    }

    const leaderLabelsById: Record<string, string> = {};
    for (const relationship of relationships) {
      const label = nullableString(profilesById.get(relationship.leaderId)?.full_name)?.trim();
      leaderLabelsById[relationship.leaderId] = label || "Leader";
    }
    const scopeKey = buildCoachHealthScopeKey(coachId);
    return {
      input: {
        version: COACH_HEALTH_EVIDENCE_INPUT_VERSION,
        enabled: true,
        authenticatedCoachId: coachId,
        currentScopeKey: scopeKey,
        snapshotScopeKey: scopeKey,
        asOf: asOf.value,
        portfolioState: "ready",
        relationships: relationships.map((relationship) => ({
          coachId,
          leaderId: relationship.leaderId,
          relationshipRevision: relationship.revision,
          relationshipStartedAt: relationship.createdAt,
          lifecycle: relationship.lifecycle,
          accessState: access.accessByLeaderId.get(relationship.leaderId) ?? "hard_gated",
          authorized: true,
          profileResolved: true,
        })),
        sourceReadiness: sourceReadiness(failedTables, unavailableSourceTypes),
        evidence,
      },
      leaderLabelsById,
      timeZone,
    };
  } catch (error) {
    if (error instanceof CoachHealthEvidenceIncompleteError) throw error;
    throw new CoachHealthEvidenceIncompleteError();
  }
}
